import type { EmployeeHealthInformationDto } from "@/generated/model/employeeHealthInformationDto";
import { useProfileSectionEditing } from "@/hooks/use-profile-section-form";
import {
  fitToWorkLabel,
  lastUpdatedLabel,
  textOrNotRecorded,
} from "@/lib/people";

import { EditHealthInformationForm } from "../forms/EditHealthInformationForm";
import { ProfileSectionCard } from "../ProfileSectionCard";
import { ProfileSectionEditButton } from "../ProfileSectionEditButton";
import { ConfidentialNote } from "../SensitiveSectionNotes";

interface HealthInformationSectionProps {
  nurseryId: string;
  userId: string;
  healthInformation: EmployeeHealthInformationDto;
  canEdit: boolean;
}

export function HealthInformationSection({
  nurseryId,
  userId,
  healthInformation,
  canEdit,
}: HealthInformationSectionProps) {
  const { editing, editButtonRef, startEditing, stopEditing } =
    useProfileSectionEditing();

  return (
    <ProfileSectionCard
      title="Health information"
      testId="profile-section-health-information"
      action={
        canEdit &&
        !editing && (
          <ProfileSectionEditButton
            ref={editButtonRef}
            section="health information"
            onClick={startEditing}
          />
        )
      }
      editForm={
        canEdit &&
        editing && (
          <EditHealthInformationForm
            nurseryId={nurseryId}
            userId={userId}
            healthInformation={healthInformation}
            onDone={stopEditing}
          />
        )
      }
      fields={[
        {
          label: "Significant medical conditions",
          value: textOrNotRecorded(
            healthInformation.significantMedicalConditions,
          ),
        },
        {
          label: "Additional information",
          value: textOrNotRecorded(healthInformation.additionalInformation),
        },
        {
          label: "Reasonable adjustments",
          value: textOrNotRecorded(healthInformation.reasonableAdjustments),
        },
        {
          label: "Fit to work",
          value: fitToWorkLabel(healthInformation.fitToWorkStatus),
        },
        {
          label: "Last updated",
          value: lastUpdatedLabel(
            healthInformation.lastUpdatedAt,
            healthInformation.lastUpdatedBy,
          ),
        },
      ]}
    >
      <ConfidentialNote text="Health information is confidential. Only you and Deputy Manager and above can see it." />
    </ProfileSectionCard>
  );
}
